import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowClockwise } from '@phosphor-icons/react'
import useSessionStore from 'Context/useSessionStore'
import BillingPlan from './BillingPlan'
import { GET_LIST_PLAN_PACKAGES, PLAN_PACKAGE_FREE } from 'Utils/const/packages'
import { PRICING_ROUTER } from 'constant/router'
import { useCompany } from 'Hooks'
import { Loading } from 'Organisms/Loading'
import { clearPendingCheckout, getPendingCheckout } from 'Utils/pendingCheckout'
import PaymentMethod from './PaymentMethod'
import BillingInformation from './BillingInformation'
import InvoicesSectionBillingSettings from './InvoicesSectionBillingSettings'

const BillingSettings = () => {
  const { user } = useSessionStore((state) => ({ user: state.user }))
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [companyPlan, setCompanyPlan] = useState(null)
  const [pendingCheckout, setPendingCheckoutState] = useState(() => getPendingCheckout())

  const { companyInfoHook, getCompanyInfoHook } = useCompany()

  // A downgraded company keeps its old subscription_id, so gate the billing
  // sections on the current plan being a paid one rather than on that id.
  const hasPaidPlan =
    !!companyInfoHook?.company_plan_package?.plan_package_id &&
    companyInfoHook.company_plan_package.plan_package_id !== PLAN_PACKAGE_FREE.id

  const getCompanyInfo = async () => {
    const res = await getCompanyInfoHook({ companyId: user.company_id })

    if (!res.success) return

    const planlist = GET_LIST_PLAN_PACKAGES({ period: res?.data?.company_plan_package?.period })

    setCompanyPlan(Object.values(planlist).find((item) => item.id === res.data.company_plan_package.plan_package_id))
  }

  useEffect(() => {
    ;(async () => {
      try {
        await getCompanyInfo()
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // Once the plan is actually paid (webhook or status check landed), the pending
  // marker has served its purpose.
  useEffect(() => {
    if (hasPaidPlan && pendingCheckout) {
      clearPendingCheckout()
      setPendingCheckoutState(null)
    }
  }, [hasPaidPlan, pendingCheckout])

  const goCheckStatus = () => {
    const query = pendingCheckout?.sessionId ? `?checkout=success&session_id=${pendingCheckout.sessionId}` : '?checkout=success'
    navigate(`${PRICING_ROUTER}${query}`)
  }

  return (
    <div className="col-12 d-grid align-content-start">
      {loading && <Loading />}
      <div className="row mb-2 justify-content-between gy-sm-0 gy-4 align-items-center">
        <div className="col-sm-auto col-12 d-flex align-items-center">
          <h2>Billing</h2>
        </div>
      </div>
      <div className="row mb-2">
        <p>Manage your information and invoices.</p>
      </div>

      {!hasPaidPlan && pendingCheckout && (
        <div className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2" role="alert">
          <span>Your upgrade is being activated. This usually only takes a moment.</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={goCheckStatus}>
            <ArrowClockwise className="me-2" size={16} />
            Check status
          </button>
        </div>
      )}

      {companyPlan && (
        <BillingPlan
          plan={companyPlan}
          expirationDate={companyInfoHook?.company_plan_package?.expiration_date}
          seats={companyInfoHook?.company_plan_package?.seats}
          period={companyInfoHook?.company_plan_package?.period}
          isTrial={companyInfoHook.isTrial}
          expire_trial={companyInfoHook.expire_trial}
          onRefreshCompany={getCompanyInfo}
          isCancel={!!companyInfoHook?.company_plan_package?.cancel_plan}
        />
      )}

      {/* Payment method, billing information and billing history all depend on a
          Stripe subscription/customer existing. Until the company subscribes,
          these sections have nothing to show and their endpoints error, so hide
          the whole block and leave only the plan selector above. */}
      {hasPaidPlan && (
        <>
          <PaymentMethod />
          <hr />
          <BillingInformation billing={companyInfoHook.billing_information} onRefresh={getCompanyInfo} />
          <hr />
          <InvoicesSectionBillingSettings />
        </>
      )}
    </div>
  )
}

export default BillingSettings
